/**
 * 单张产出落盘 —— **全站唯一一份下载实现**。
 *
 * 之前 `ResultGrid` 与 `MyWorks` 各写了一份同样的 `saveAs`，
 * 而且两份都只覆盖「同源 / 带 CORS」的情况。这里补上第三种：
 * 图床（`i.ibb.co`、`cdn3.dmiapi.com`）不一定回 `Access-Control-Allow-Origin`，
 * 一旦没有，`fetch` 会直接抛 —— 旧实现会掉进 `catch` 退化成新窗口打开，
 * 看起来「下载按钮点了但没下载」。
 */

/** 从 URL 猜一个像样的后缀（图床地址常带 query，不处理会存成 `.png?e=...`） */
function extOf(url: string, fallback: string): string {
  try {
    const path = new URL(url, window.location.href).pathname
    const m = /\.([a-zA-Z0-9]{2,5})$/.exec(path)
    return m ? m[1].toLowerCase() : fallback
  } catch {
    return fallback
  }
}

/**
 * 下载一个远程资源。
 *
 * 三级降级，**任何一级失败都要让用户拿到东西**：
 * 1. `fetch` → blob → `a[download]`（能指定文件名，体验最好）
 * 2. 直接给 `<a download href>`（同源资源不需要 fetch 也能下）
 * 3. 新窗口打开（跨域且拿不到 blob 时的最后手段，用户可自己另存）
 */
export async function saveAs(url: string, name: string, fallbackExt = 'png'): Promise<void> {
  const fileName = /\.[a-zA-Z0-9]{2,5}$/.test(name)
    ? name
    : `${name}.${extOf(url, fallbackExt)}`

  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const blob = await res.blob()
    const href = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = href
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    a.remove()
    // 立刻 revoke 会让部分浏览器来不及取数据，延后一拍
    window.setTimeout(() => URL.revokeObjectURL(href), 4000)
    return
  } catch {
    /* 落到下一级 */
  }

  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.target = '_blank'
  a.rel = 'noreferrer'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

/**
 * 落一个**文本**文件（CSV / TXT）—— 全站唯一一份。
 *
 * 之前 `UsageAudit` 的「导出 CSV」在页面里自己拼了一遍
 * `Blob → createObjectURL → a.click → revokeObjectURL`。
 * 批量出图的模板下载与提示词导出也要落文本，再抄第三遍就是三份各管一段的
 * 同一种实现（本项目铁律「同一实现只许一份」）。统一收到这里。
 *
 * `bom` 默认开：Excel（尤其中文环境）不认没有 BOM 的 UTF-8，
 * 导出的中文列名会变成乱码 —— 而 CSV 的下一站通常就是 Excel。
 */
export function saveText(name: string, text: string, mime = 'text/plain', bom = true): void {
  const blob = new Blob([bom ? `\ufeff${text}` : text], { type: `${mime};charset=utf-8` })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  // 立刻 revoke 会让部分浏览器来不及取到 blob（下载落空，且没有任何报错）
  window.setTimeout(() => URL.revokeObjectURL(url), 4000)
}

/** 批量下载：**串行**并留间隔。
 *
 * 并发 10 个 `a.click()` 在 Chrome 里会被判定成「多文件下载」而弹权限框，
 * 甚至只落第一个 —— 串行慢一点，但每一个都真的落盘。
 */
export async function saveAllAs(
  items: { url: string; name: string }[],
  onProgress?: (done: number, total: number) => void,
): Promise<void> {
  for (let i = 0; i < items.length; i += 1) {
    await saveAs(items[i].url, items[i].name)
    onProgress?.(i + 1, items.length)
    if (i < items.length - 1) await new Promise((r) => window.setTimeout(r, 450))
  }
}
